export type CaseStatus = "open" | "waiting" | "escalated" | "resolved";

// --- AI content validation (tiered QA) ------------------------------------
// Mirrors workers/api/src/lib/validation.ts. Every AI-generation response
// carries an optional `validation` of this shape; the ValidationBadge
// renders it and (on FAIL) gates export/copy/send until acknowledged.
export type ValidationStatus = "pass" | "warn" | "fail";
export type ValidationSeverity = "error" | "warning" | "info";

export interface ValidationIssue {
  // Stable id within a result; per-finding actions are keyed off this.
  id?: string;
  type: string;
  severity: ValidationSeverity;
  description: string;
  // Verbatim text the finding refers to, for placing the inline marker.
  locator?: string;
  // Self-contained card context so a reviewer can accept/dismiss without
  // opening the source record. Optional — older stored blobs lack it.
  context?: FindingContext;
}

// Mirrors workers/api/src/lib/validation/types.ts FindingContext. Each
// field is optional; renderers show only what's present.
export interface FindingContext {
  // The source record this finding traces to (when resolvable).
  record_id?: string;
  record_date?: string; // YYYY-MM-DD
  record_subject?: string;
  record_type?: string; // source_type
  record_from?: string;
  record_to?: string;
  record_excerpt?: string; // first ~150 chars of the body
  record_summary?: string; // "This record discusses: …"
  attachment_count?: number;
  body_truncated?: boolean;

  // The discrepancy (AI vs. record).
  ai_stated?: string;
  record_shows?: string;
  record_quote?: string;
  generated_sentence?: string;
  no_citation?: boolean;

  // Citation findings.
  citation_ref?: string;
  citation_target?: string;
}

export type FindingActionKind = "dismissed" | "accepted" | "flagged";
export interface FindingAction {
  finding_id: string;
  action: FindingActionKind;
  // Optional free-text correction note the user attaches when flagging a
  // finding "for edit". Sent to the server and fed into the regeneration
  // prompt. Only meaningful for "flagged".
  note?: string;
}

export interface AuditorReport {
  name:
    | "chronology"
    | "financial"
    | "attribution"
    | "completeness"
    | "hallucination";
  ran: boolean;
  ok: boolean;
  issues: ValidationIssue[];
}

export interface ValidationResult {
  status: ValidationStatus;
  issues: ValidationIssue[];
  tier2_ran: boolean;
  // True on the immediate response when Tier 2 was dispatched to the
  // background — the frontend shows "Verifying…" and polls the cached
  // GET until the stored result clears this flag (tier2_ran becomes true).
  tier2_pending?: boolean;
  auditors?: AuditorReport[];
  // Per-finding user decisions (dismiss/accept/flag), persisted with the
  // result so they survive reload; cleared when content is regenerated.
  actions?: FindingAction[];
  checked_at: string;
  // Set client-side when background Tier-2 verification didn't resolve
  // in time (poll timeout / stale pending). Tier-1 results stand; this
  // explains why the deeper audit is absent. Never persisted server-side.
  note?: string;
}

// Which upstream provider a BYOK user's stored key belongs to. The
// hosted product always uses Anthropic — this is read only when a
// BYOK key is configured.
export type ByoProvider = "anthropic" | "openai" | "gemini";

// One row in the visual timeline. Each event is built from exactly
// one record on the backend, so `record_id` is always present and
// "View source" navigation is deterministic. Stored as a JSON array
// on cases.timeline_data and rendered both in-app (HTML/CSS) and
// in the PDF export (direct jsPDF draw).
export interface TimelineEvent {
  record_id: string;
  date: string; // YYYY-MM-DD
  title: string;
  detail: string;
  party: string;
  direction: "inbound" | "outbound" | "internal";
  significance: "high" | "medium" | "low";
  type:
    | "email"
    | "letter"
    | "phone"
    | "payment"
    | "deadline"
    | "commitment"
    | "filing"
    | "other";
  amount: string | null;
  // Structured fields introduced alongside the unified timeline. The
  // server populates them on every new generation and synthesises
  // them from the scalars above when reading legacy cached rows, so
  // consumers can treat them as always-present in practice. Optional
  // on the wire for forward/backward compatibility.
  summary?: string;
  parties?: string[];
  amounts?: string[];
  citations?: string[];
  // Set only on events split out of a multi-entry record (chat export,
  // email thread, call log): the parent record's subject. Several events
  // can share one record_id; this lets the card show "part of <subject>"
  // so the user understands they trace back to one source.
  source_subject?: string;
}

// One entry in the fact-based timeline (Phase 2). Assembled deterministically
// on the server from a record plus its verified facts — no AI. Drives both the
// Visual and Written timeline views; every word shown is record metadata or a
// source-verified verbatim quote.
export interface TimelineRecord {
  id: string;
  date: string; // YYYY-MM-DD
  subject: string | null;
  type: string; // source_type
  from: string | null;
  to: string | null;
  direction: string; // "inbound" | "outbound"
  significance: Significance;
  content_trust: string; // "high" | "low"
  facts: RecordFact[];
  attachment_count: number;
  has_files: boolean;
}

// Snapshot from GET /api/ai/usage-meter. `limit` and `warn_threshold`
// are null for BYOK users (no CaseThread-side ceiling applies).
export interface AiUsageMeter {
  ai_calls: number;
  // Breakdown of ai_calls this period: generation/retry calls vs Tier-2
  // quality-audit calls (task_type 'quality_audit'). Optional for back-compat.
  generation_calls?: number;
  auditor_calls?: number;
  limit: number | null;
  warn_threshold: number | null;
  reset_at: string;
  byo_key_active: boolean;
  byo_key_set_at: string | null;
  byo_provider: ByoProvider | null;
  byo_model: string | null;
  allowed: boolean;
}

export interface Case {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  forward_address: string;
  // Readable email slug (local-part). Primary address shown in the
  // UI; editable via PATCH /api/cases/:id/email-slug. Null briefly
  // for legacy rows — the API backfills on next access.
  email_slug: string | null;
  created_at: string;
  status: CaseStatus;
  deadline: string | null;
  summary: string | null;
  summary_updated_at: string | null;
  // JSON-encoded ValidationResult blobs (raw strings from D1) for each
  // cached AI artefact. Parsed client-side for the verification badges and
  // the aggregate export badge.
  summary_validation?: string | null;
  costs_validation?: string | null;
  key_facts?: string | null;
  key_facts_updated_at?: string | null;
  previous_slugs?: string; // JSON array string
  story?: string | null;
  story_updated_at?: string | null;
  group_name: string | null;
}

export interface CaseGroup {
  name: string;
  count: number;
}

export interface Contact {
  id: string;
  case_id: string;
  name: string;
  role: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  created_at: string;
}

export interface CaseLink {
  link_id: string;
  note: string | null;
  created_at: string;
  id: string; // linked case id
  name: string;
  status: CaseStatus;
  description: string | null;
}

export interface Share {
  id: string;
  case_id: string;
  token: string;
  created_at: string;
  expires_at: string;
  revoked_at: string | null;
}

export interface Annotation {
  id: string;
  record_id: string;
  selected_text: string;
  note: string;
  created_at: string;
}

export const CASE_STATUS_LABELS: Record<CaseStatus, string> = {
  open: "Open",
  waiting: "Waiting for response",
  escalated: "Escalated",
  resolved: "Resolved",
};

export const SIGNIFICANCE_LABELS: Record<Significance, string> = {
  high: "Key",
  medium: "Notable",
  low: "Routine",
};

export const SIGNIFICANCE_EMOJI: Record<Significance, string> = {
  high: "🔴",
  medium: "🟡",
  low: "⚪",
};

export const KEY_FACT_EMOJI: Record<KeyFact["type"], string> = {
  amount: "💰",
  commitment: "🤝",
  deadline: "⏰",
  admission: "✅",
  denial: "❌",
  person: "👤",
  "reference-number": "🔢",
};

// A single source-verified fact extracted from one record (migration
// 0041). Only verified facts are ever returned to the client.
export type FactType =
  | "commitment"
  | "amount"
  | "decision"
  | "action"
  | "request"
  | "deadline"
  | "outcome"
  | "statement"
  | "event";

export interface RecordFact {
  id: string;
  record_id: string;
  case_id: string;
  fact_type: string;
  date: string | null;
  party: string | null;
  verbatim_quote: string;
  summary: string;
  amount: string | null;
  deadline: string | null;
  verified: number;
  verification_note: string | null;
  created_at: string;
  // 'high' (default) or 'low' (extracted from an audio transcript).
  trust_level?: string;
  // Classification: 'case_event' (default), 'background', 'coverage_term',
  // or 'valuation'. Drives which overview section a fact appears in.
  fact_category?: FactCategory;
  // Financial flow direction: 'cost', 'recovery', 'offer', or 'rate'.
  // NULL on non-amount facts and on legacy rows extracted before the
  // amount_flow migration — display treats NULL as 'cost'.
  amount_flow?: string | null;
}

// One flagged record from the extraction-run drop report (zero-drop
// invariant): re-extraction produced dramatically fewer facts than the
// previous run, or the model returned an empty set and the old facts were
// preserved. Mirrors workers/api/src/lib/extraction-report.ts.
export interface ExtractionDropFlag {
  record_id: string;
  subject: string | null;
  before: number;
  after: number;
  reason: "dropped_to_zero" | "large_drop" | "empty_response_preserved";
}

export type FactCategory =
  | "case_event"
  | "background"
  | "coverage_term"
  | "valuation";

export type Direction = "inbound" | "outbound";
export type SourceType =
  | "email"
  | "bank_portal"
  | "phone_call"
  | "letter"
  | "sms"
  | "in_person"
  | "video"
  | "other";

export interface CaseRecord {
  id: string;
  case_id: string;
  record_date: string;
  logged_at: string;
  direction: Direction;
  source_type: SourceType;
  from_addr: string | null;
  to_addr: string | null;
  subject: string | null;
  body_text: string | null;
  image_r2_key: string | null;
  ocr_text: string | null;
  notes: string | null;
  email_message_id: string | null;
  file_r2_key: string | null;
  file_name: string | null;
  file_mime_type: string | null;
  forwarded_via: string | null;
  pinned: number; // 0/1 from SQLite
  is_duplicate: number; // 0/1
  duplicate_of: string | null;
  thread_id: string | null;
  thread_position: number;
  significance: Significance;
  significance_reason: string | null;
  // Short deterministic content-derived descriptor from the record's verified
  // facts (migration 0055). Supplements the subject in list/source views; null
  // when the record has no facts. See lib/recordMeta describeRecord.
  content_label?: string | null;
  // Deterministic document-kind bucket (migration 0056) + the user-confirmed
  // override, if any. Effective category = category_override ?? record_category
  // ?? 'other'. See lib/recordCategory. category_override is served by the
  // records list endpoint (a join on record_category_overrides).
  record_category?: string | null;
  category_override?: string | null;
  tags: string; // comma-separated, lowercased
  source_content: string | null;
  source_hash: string | null;
  is_edited: number; // 0/1
  // Populated by GET /api/cases/:id/records — list view only, without
  // the full extracted_text. Detail view pulls the text lazily.
  attachments?: RecordAttachment[];
  // JSON string or null — populated when OCR detected a receipt. The
  // UI parses it lazily (see ReceiptDetected panel) so this stays a
  // string to match the DB column shape.
  receipt_data?: string | null;
  // The volume this record belongs to. Populated by the backend on
  // every insert + backfilled by migration 0031. Used by the Timeline
  // to group records under their volume headers.
  volume_id?: string | null;
  // JSON-encoded per-field confidence tag (see migration 0035). Null
  // on legacy rows that predate the column; the UI treats null as
  // "don't show indicators" rather than defaulting to a colour.
  extraction_confidence?: string | null;
  // How the record came into existence — see migration 0036.
  // "manual" records (written via the Write record modal) render
  // with a distinct pen icon so users can spot them at a glance.
  creation_source?: "upload" | "email" | "manual";
  // JSON-encoded RecordAuditReport from the deterministic extraction-audit
  // layer (migration 0040). Null until the record has been audited.
  extraction_audit?: string | null;
  // Provenance of record_date (see migration 0037). Null on legacy
  // rows; the UI shows a subtle label in the expanded record so the
  // reader can tell whether the date came from an authoritative
  // header or a weaker signal like body-text extraction.
  date_source?:
    | "header"
    | "forwarded_header"
    | "body"
    | "upload"
    | "manual"
    | null;
}

// Parsed shape of records.extraction_confidence. The UI reads the
// raw string off each record, parses it with parseExtractionConfidence,
// and renders a coloured dot next to each metadata field.
export interface ExtractionConfidence {
  date: "regex_header" | "regex_single" | "ai_selected" | "fallback";
  from: "header" | "ai" | "none";
  to: "header" | "ai" | "none";
  subject: "header" | "ai" | "none";
}

export function parseExtractionConfidence(
  raw: string | null | undefined,
): ExtractionConfidence | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ExtractionConfidence>;
    if (!parsed || typeof parsed !== "object") return null;
    const date = parsed.date;
    const from = parsed.from;
    const to = parsed.to;
    const subject = parsed.subject;
    if (
      date !== "regex_header" &&
      date !== "regex_single" &&
      date !== "ai_selected" &&
      date !== "fallback"
    ) {
      return null;
    }
    return {
      date,
      from: from === "header" || from === "ai" ? from : "none",
      to: to === "header" || to === "ai" ? to : "none",
      subject:
        subject === "header" || subject === "ai" ? subject : "none",
    };
  } catch {
    return null;
  }
}

// Parsed form of records.receipt_data. The server emits this shape
// inside uploadFile responses; after persistence it's re-read from
// the DB as a JSON string and parsed client-side when rendering.
export interface ReceiptData {
  is_receipt: true;
  vendor: string | null;
  date: string | null;
  amount: number | null;
  currency: string | null;
  description: string | null;
}

export type CostCategory =
  | "travel"
  | "medical"
  | "accommodation"
  | "replacement"
  | "legal"
  | "other";

export interface CostItem {
  date: string | null;
  amount: number;
  currency: string;
  description: string;
  category: CostCategory;
  record_id: string | null;
  confidence: "high" | "medium" | "low";
}

export interface CostsSummary {
  total_usd: number;
  currency: string;
  currency_symbol: string;
  items: CostItem[];
  summary: string;
  generated_at: string;
}

// A volume is a "chapter" of a case's history. Closed volumes carry a
// cached AI-generated summary + key facts; the current (open) volume
// has closed_at: null. Single-volume cases render with no volume UI
// — see CaseThread-Volumes-Design.md.
export interface CaseVolume {
  id: string;
  volume_number: number;
  title: string | null;
  record_count: number;
  estimated_tokens: number;
  first_record_date: string | null;
  last_record_date: string | null;
  opened_at: string;
  closed_at: string | null;
  has_summary: boolean;
  has_key_facts: boolean;
}

export interface VolumeSummaryPayload {
  volume: CaseVolume;
  summary: string | null;
  key_facts: KeyFact[];
  validation?: ValidationResult | null;
}

export type Significance = "high" | "medium" | "low";

export interface KeyFact {
  type:
    | "amount"
    | "commitment"
    | "deadline"
    | "admission"
    | "denial"
    | "person"
    | "reference-number";
  fact: string;
  // Optional YYYY-MM-DD of the source record. Absent on facts
  // generated before the citation feature shipped — renderers treat
  // that as "no source link", no errors.
  source?: string;
}

export interface RecordVersion {
  id: string;
  record_id: string;
  content: string;
  modified_at: string;
  reason: string | null;
  version_number: number;
}

export interface RecordAttachment {
  id: string;
  record_id: string;
  filename: string;
  mime_type: string | null;
  r2_key: string;
  size_bytes: number | null;
  created_at: string;
  has_text: number; // 0/1 — whether extracted_text is populated
}

export interface AttachmentDetail {
  id: string;
  record_id: string;
  filename: string;
  mime_type: string | null;
  r2_key: string;
  size_bytes: number | null;
  extracted_text: string | null;
  created_at: string;
}

export type FileKind =
  | "pdf"
  | "docx"
  | "xlsx"
  | "image"
  | "audio"
  | "video"
  | "other";

export interface FileUploadResult {
  file_r2_key: string;
  file_name: string;
  file_mime_type: string;
  file_kind: FileKind;
  truncated: boolean;
  extracted: {
    record_date: string | null;
    from_addr: string | null;
    to_addr: string | null;
    subject: string | null;
    body_text: string | null;
    ocr_text: string | null;
    source_type: string | null;
    direction: string | null;
    receipt_data: ReceiptData | null;
    extraction_confidence: ExtractionConfidence;
    date_source:
      | "header"
      | "forwarded_header"
      | "body"
      | "upload"
      | "manual";
  };
  // Null when extraction succeeded. When set, the file still landed in
  // R2 and the record will be created, but OCR/text-extraction raised.
  extraction_error: string | null;
}

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

export const SOURCE_TYPE_LABELS: Record<SourceType, string> = {
  email: "Email",
  bank_portal: "Bank portal",
  phone_call: "Phone call",
  letter: "Letter",
  sms: "SMS",
  in_person: "In person",
  video: "Video",
  other: "Other",
};

// Single source of truth for which source_type values the UI + API accept.
// Keep in sync with workers/api/src/types.ts VALID_SOURCES.
export const VALID_SOURCES: SourceType[] = [
  "email",
  "bank_portal",
  "phone_call",
  "letter",
  "sms",
  "in_person",
  "video",
  "other",
];

// --- Deterministic extraction audit (mirrors workers extraction-audit) ---
export type AuditStatus = "verified" | "flagged" | "no_source_text";
export interface AuditResult {
  check:
    | "date"
    | "sender"
    | "subject"
    | "amounts"
    | "significance"
    | "ocr"
    | "transcription"
    | "attachments"
    | "thread";
  status: AuditStatus;
  issues: string[];
}
export interface RecordAuditReport {
  checks: AuditResult[];
  flagged: boolean;
  audited_at: string;
}
